/**
 * Browser Firestore adaptor.
 */

import { getApps, initializeApp } from 'firebase/app';
import {
  DocumentReference,
  DocumentSnapshot,
  FieldValue as FirestoreFieldValue,
  Firestore,
  arrayRemove,
  arrayUnion,
  deleteDoc,
  deleteField,
  getDoc,
  getFirestore,
  increment as firestoreIncrement,
  serverTimestamp,
} from 'firebase/firestore';
import { FirestoreScheduler } from '../firestore-scheduler';
export {
  AggregateField,
  AggregateQuerySnapshot,
  AggregateSpec,
  CollectionReference,
  DocumentChange,
  DocumentChangeType,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  FieldPath,
  OrderByDirection,
  Query,
  QueryConstraint,
  QueryDocumentSnapshot,
  QuerySnapshot,
  SetOptions,
  Timestamp,
  Transaction,
  UpdateData,
  WhereFilterOp,
  WriteBatch,
  average,
  collection,
  collectionGroup,
  count,
  deleteField,
  doc,
  endBefore,
  getAggregateFromServer,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  setDoc,
  startAfter,
  startAt,
  sum,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

/**
 * Re-export the Firestore sentinel type so that `type FieldValue` resolves to
 * the actual sentinel class (used by `isFieldValue` and
 * `PartialWithFieldValue`), while the *value* `FieldValue` below provides the
 * convenience wrapper methods (`FieldValue.delete()`, etc.).
 */
export type FieldValue = FirestoreFieldValue;

/**
 * Browser-compatible FieldValue wrapper.
 *
 * The modular Firebase client SDK (`firebase/firestore` v9+) does not provide
 * static methods on the `FieldValue` class — it uses standalone functions
 * (`deleteField`, `increment`, etc.) instead. The admin SDK
 * (`firebase-admin/firestore`) still exposes `FieldValue.delete()`,
 * `FieldValue.increment()`, etc.
 *
 * This wrapper bridges the gap so call-sites can use `FieldValue.delete()`,
 * `FieldValue.increment()`, etc. in both environments.
 */
// eslint-disable-next-line no-redeclare, @typescript-eslint/naming-convention
export const FieldValue = {
  delete: deleteField,
  increment,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
};

export function store(appName?: string): Firestore {
  const determinedAppName =
    appName ?? FirestoreScheduler.appName ?? '[DEFAULT]';
  const existingApp = getApps().find(
    (app) => app && app.name === determinedAppName
  );
  const firebaseApp =
    existingApp ?? initializeApp(FirestoreScheduler.options, determinedAppName);

  return getFirestore(firebaseApp);
}

/**
 * @deprecated
 * Use `Firestore.docExists` instead.
 */
export async function docExists<T extends object>(
  ref: DocumentReference<T> | DocumentSnapshot<T>
): Promise<boolean> {
  if (ref instanceof DocumentReference) {
    return (await getDoc(ref)).exists();
  }
  return ref.exists();
}

export async function hardDeleteDoc<T extends object>(
  ref: DocumentReference<T>
): Promise<void> {
  return deleteDoc(ref);
}

export function increment(value: number): FieldValue {
  return firestoreIncrement(value);
}

// export type Query<T> = FirestoreQuery<T>;
// export type QuerySnapshot<T> = FirestoreQuerySnapshot<T>;
// export type QueryDocumentSnapshot<T> = FirestoreQueryDocumentSnapshot<T>;
// export type DocumentSnapshot<T> = FirestoreDocumentSnapshot<T>;
// export type SetOptions = FirestoreSetOptions;
// export type DocumentReference<T = unknown> = FirestoreDocumentReference<T>;
// export type DocumentChangeType = FirestoreDocumentChangeType;
// export type DocumentChange = FirestoreDocumentChange;
// export type DocumentData = FirestoreDocumentData;
// export type Timestamp = FirestoreTimestamp;
// export type CollectionReference<T> = FirestoreCollectionReference<T>;
// export type OrderByDirection = FirestoreOrderByDirection;
// export type WhereFilterOp = FirestoreWhereFilterOp;
// export type Transaction = FirestoreTransaction;
// export type WriteBatch = FirestoreWriteBatch;
// export type FieldValue = FirestoreFieldValue;
// export type FieldPath = FirestoreFieldPath;

// eslint-disable-next-line no-redeclare, @typescript-eslint/naming-convention
// export const DocumentReference: typeof FirestoreDocumentReference =
//   FirestoreDocumentReference;

// eslint-disable-next-line no-redeclare, @typescript-eslint/naming-convention
// export const DocumentSnapshot: typeof FirestoreDocumentSnapshot =
//   FirestoreDocumentSnapshot;

// eslint-disable-next-line no-redeclare, @typescript-eslint/naming-convention
// export const Timestamp: typeof FirestoreTimestamp = FirestoreTimestamp;

// eslint-disable-next-line no-redeclare, @typescript-eslint/naming-convention
// export const FieldValue: typeof FirestoreFieldValue = FirestoreFieldValue;

// eslint-disable-next-line no-redeclare, @typescript-eslint/naming-convention
// export const FieldPath: typeof FirestoreFieldPath = FirestoreFieldPath;

// eslint-disable-next-line no-redeclare, @typescript-eslint/naming-convention
// export const Transaction: typeof FirestoreTransaction = FirestoreTransaction;

// interface ITransaction {
//   get<T>(documentRef: DocumentReference<T>): Promise<DocumentSnapshot<T>>;
//   set<T>(
//     documentRef: DocumentReference<T>,
//     data: Partial<T>,
//     options: SetOptions
//   ): ITransaction;
//   set<T>(documentRef: DocumentReference<T>, data: T): ITransaction;
//   update<T>(documentRef: DocumentReference<T>, data: Partial<T>): ITransaction;
//   delete<T>(documentRef: DocumentReference<T>): Transaction;
// }

/**
 * Distance measure options for vector search.
 * Browser stub - vector search is only available on the server.
 */
export type DistanceMeasure = 'EUCLIDEAN' | 'COSINE' | 'DOT_PRODUCT';

/**
 * Options for findNearest vector search.
 * Browser stub - vector search is only available on the server.
 */
export interface IFindNearestOptions {
  limit: number;
  distanceMeasure: DistanceMeasure;
}

/**
 * VectorQuery type stub for browser.
 * Vector search is only available on the server (Firebase Admin SDK).
 */
/* eslint-disable @typescript-eslint/no-unused-vars */
export class VectorQuery<
  _AppModelType = object,
  _DbModelType extends object = object,
> {}
/* eslint-enable @typescript-eslint/no-unused-vars */

/**
 * VectorQuerySnapshot type stub for browser.
 * Vector search is only available on the server (Firebase Admin SDK).
 */
/* eslint-disable @typescript-eslint/no-unused-vars */
export class VectorQuerySnapshot<
  _AppModelType = object,
  _DbModelType extends object = object,
> {}
/* eslint-enable @typescript-eslint/no-unused-vars */

/**
 * Browser stub for findNearest - throws an error as vector search is server-only.
 *
 * @throws Error - Always throws as vector search is not available in the browser
 */
export function findNearest<T>(
  _queryFn: unknown,
  _vectorField: string,
  _queryVector: number[],
  _options: IFindNearestOptions
): VectorQuery<T> {
  throw new Error(
    'findNearest is only available on the server (Firebase Admin SDK). ' +
      'Vector search cannot be performed in the browser.'
  );
}
